import { useState } from "react";
import type { SolvePhase } from "@playbookai/types";
import { useSetPhaseLock } from "./useSetPhaseLock.js";

/**
 * View-model for the per-phase lock toggles (ADR-0019). Wraps `useSetPhaseLock`
 * and tracks the in-flight phases in a Set.
 *
 * A Set — not `useMutation().variables` — is required: `variables` only reflects
 * the LATEST mutate() call, so a second phase's toggle would clear the pending
 * state of an earlier in-flight phase and re-enable it. Keying by phase disables
 * each pending toggle independently, which is what blocks a phase from being
 * toggled twice concurrently (overlapping requests can resolve out of order and
 * leave the cached lock state wrong).
 *
 * Cleanup is bound to each call's own `mutateAsync()` promise, NOT the shared
 * mutation observer: per-call `mutate()` callbacks live on the observer and are
 * overwritten by the next call, so the first settle would run the second call's
 * cleanup (the same latest-only pitfall as `variables`).
 */
export function usePhaseLockToggle(solveId: string): {
  pendingPhases: ReadonlySet<SolvePhase>;
  togglePhaseLock: (phase: SolvePhase, locked: boolean) => void;
} {
  const setPhaseLock = useSetPhaseLock();
  const [pendingPhases, setPendingPhases] = useState<ReadonlySet<SolvePhase>>(
    () => new Set(),
  );

  const togglePhaseLock = (phase: SolvePhase, locked: boolean): void => {
    // Defensive: skip a phase already in flight. The strip disables its toggle
    // while pending, but the hook shouldn't depend on caller discipline — a
    // second request could still resolve out of order against the first.
    if (pendingPhases.has(phase)) return;
    setPendingPhases((prev) => new Set(prev).add(phase));
    void setPhaseLock
      .mutateAsync({ solveId, phase, locked })
      // The hook's onError surfaces the toast; swallow the rejection here so the
      // per-call cleanup still runs without an unhandled promise rejection.
      .catch(() => undefined)
      .finally(() => {
        setPendingPhases((prev) => {
          const next = new Set(prev);
          next.delete(phase);
          return next;
        });
      });
  };

  return { pendingPhases, togglePhaseLock };
}
